import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Project,
  ProjectDatabaseCredentials,
} from '@claude-code-api/shared';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import { toast } from 'sonner';
import {
  Database,
  Key,
  Table as TableIcon,
  RefreshCw,
  Check,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

interface DatabaseTable {
  name: string;
  type: 'table' | 'view';
  sizeBytes: number;
  columnCount: number;
}

interface DatabaseColumn {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue: string | null;
}

interface TableRowsData {
  rows: Record<string, unknown>[];
  totalCount: number;
  primaryKey: string | null;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatCellValue(value: unknown, nullLabel = 'NULL'): string {
  if (value === null) return nullLabel;
  if (value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return JSON.stringify(value);
  }
  return String(value);
}

interface ProjectDatabaseTabProps {
  project: Project;
}

export function ProjectDatabaseTab({ project }: ProjectDatabaseTabProps) {
  const { t } = useTranslation(['projects', 'common']);
  const [databaseAvailable, setDatabaseAvailable] = useState(
    project.hasDatabase,
  );
  // Database state
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [columns, setColumns] = useState<DatabaseColumn[]>([]);
  const [credentials, setCredentials] =
    useState<ProjectDatabaseCredentials | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Table data viewer state
  const [tableData, setTableData] = useState<TableRowsData | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);

  // CRUD modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(
    null,
  );
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const response = await fetch(
        `/api/projects/${project.id}/database/tables`,
        {
          credentials: 'include',
        },
      );
      if (response.ok) {
        const data = await response.json();
        setTables(data.data);
        setDatabaseAvailable(true);
      } else if (response.status === 400) {
        // Project has no database configured.
        setTables([]);
      } else {
        toast.error(t('projects:database.errors.loadTables'));
      }
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      toast.error(t('projects:database.errors.loadTables'));
    } finally {
      setLoadingTables(false);
    }
  };

  const fetchTableData = async (tableName: string, page: number = 0) => {
    setLoadingData(true);
    setCurrentPage(page);
    try {
      const columnsResponse = await fetch(
        `/api/projects/${project.id}/database/tables/${encodeURIComponent(tableName)}`,
        { credentials: 'include' },
      );
      if (columnsResponse.ok) {
        const columnsData = await columnsResponse.json();
        setColumns(columnsData.data);
      }

      const rowsResponse = await fetch(
        `/api/projects/${project.id}/database/tables/${encodeURIComponent(tableName)}/rows?limit=${pageSize}&offset=${page * pageSize}`,
        { credentials: 'include' },
      );
      if (rowsResponse.ok) {
        const rowsData = await rowsResponse.json();
        setTableData(rowsData.data);
      }
    } catch (error) {
      console.error('Failed to fetch table data:', error);
      toast.error(t('projects:database.errors.loadTableData'));
    } finally {
      setLoadingData(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (selectedTable) {
      fetchTableData(selectedTable, newPage);
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await fetch(
        `/api/projects/${project.id}/database/credentials`,
        {
          credentials: 'include',
        },
      );
      if (response.ok) {
        const data = await response.json();
        setCredentials(data.data);
        setDatabaseAvailable(true);
      } else if (response.status === 400) {
        setCredentials(null);
      } else {
        toast.error(t('projects:database.errors.loadCredentials'));
      }
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
      toast.error(t('projects:database.errors.loadCredentials'));
    }
  };

  useEffect(() => {
    setDatabaseAvailable(project.hasDatabase);
    fetchTables();
    fetchCredentials();
  }, [project.id, project.hasDatabase]);

  useEffect(() => {
    if (selectedTable) {
      fetchTableData(selectedTable, 0);
    } else {
      setColumns([]);
      setTableData(null);
    }
  }, [selectedTable]);

  if (!databaseAvailable && !loadingTables && !credentials) {
    return (
      <div className="text-center py-12">
        <Database className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-1">
          {t('projects:database.emptyTitle')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('projects:database.emptyDescription')}
        </p>
      </div>
    );
  }

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const openAddModal = () => {
    const initialData: Record<string, string> = {};
    columns.forEach((col) => {
      initialData[col.name] = '';
    });
    setFormData(initialData);
    setFormError(null);
    setShowAddModal(true);
  };

  const openEditModal = (row: Record<string, unknown>) => {
    const initialData: Record<string, string> = {};
    columns.forEach((col) => {
      const value = row[col.name];
      initialData[col.name] =
        value === null
          ? ''
          : formatCellValue(value, t('projects:database.null'));
    });
    setFormData(initialData);
    setEditingRow(row);
    setFormError(null);
    setShowEditModal(true);
  };

  const openDeleteModal = (row: Record<string, unknown>) => {
    setEditingRow(row);
    setShowDeleteModal(true);
  };

  const handleAddRow = async () => {
    if (!selectedTable || !tableData) return;
    setSaving(true);
    setFormError(null);
    try {
      const data: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value !== '') {
          data[key] = value;
        }
      }
      const response = await fetch(
        `/api/projects/${project.id}/database/tables/${encodeURIComponent(selectedTable)}/rows`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        },
      );
      const result = await response.json();
      if (response.ok) {
        setShowAddModal(false);
        fetchTableData(selectedTable, currentPage);
      } else {
        setFormError(result.error || t('projects:database.errors.addRow'));
      }
    } catch {
      setFormError(t('projects:database.errors.addRow'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditRow = async () => {
    if (!selectedTable || !tableData?.primaryKey || !editingRow) return;
    setSaving(true);
    setFormError(null);
    try {
      const primaryKeyValue = editingRow[tableData.primaryKey];
      const data: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(formData)) {
        if (key !== tableData.primaryKey) {
          data[key] = value === '' ? null : value;
        }
      }
      const response = await fetch(
        `/api/projects/${project.id}/database/tables/${encodeURIComponent(selectedTable)}/rows/${encodeURIComponent(String(primaryKeyValue))}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            primaryKey: tableData.primaryKey,
            data,
          }),
        },
      );
      const result = await response.json();
      if (response.ok) {
        setShowEditModal(false);
        setEditingRow(null);
        fetchTableData(selectedTable, currentPage);
      } else {
        setFormError(result.error || t('projects:database.errors.updateRow'));
      }
    } catch {
      setFormError(t('projects:database.errors.updateRow'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedTable || !tableData?.primaryKey || !editingRow) return;
    setSaving(true);
    try {
      const primaryKeyValue = editingRow[tableData.primaryKey];
      const response = await fetch(
        `/api/projects/${project.id}/database/tables/${encodeURIComponent(selectedTable)}/rows/${encodeURIComponent(String(primaryKeyValue))}?primaryKey=${encodeURIComponent(tableData.primaryKey)}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );
      if (response.ok) {
        setShowDeleteModal(false);
        setEditingRow(null);
        fetchTableData(selectedTable, currentPage);
      }
    } catch (error) {
      console.error('Failed to delete row:', error);
      toast.error(t('projects:database.errors.deleteRow'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Database className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {credentials?.database ||
                  `project_${project.id.substring(0, 12)}`}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('projects:database.postgresDatabase')}
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="tables" className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-muted/30 p-0 h-auto">
            <TabsTrigger
              value="tables"
              className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background py-3"
            >
              <div className="flex items-center justify-center gap-2">
                <TableIcon className="w-4 h-4" />
                <span>{t('projects:database.tabs.tables')}</span>
                {tables.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {tables.length}
                  </Badge>
                )}
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="connection"
              className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background py-3"
            >
              <div className="flex items-center justify-center gap-2">
                <Key className="w-4 h-4" />
                <span>{t('projects:database.tabs.connection')}</span>
              </div>
            </TabsTrigger>
          </TabsList>

          {/* Connection Tab */}
          <TabsContent value="connection" className="p-6 space-y-6 mt-0">
            {credentials && (
              <>
                <div>
                  <Label className="mb-2 block">
                    {t('projects:database.connectionUrl')}
                  </Label>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 px-4 py-3 bg-muted border rounded-xl font-mono text-sm break-all">
                      {credentials.databaseUrl}
                    </div>
                    <Button
                      variant={copiedField === 'url' ? 'secondary' : 'outline'}
                      onClick={() =>
                        copyToClipboard(credentials.databaseUrl, 'url')
                      }
                      className={
                        copiedField === 'url'
                          ? 'bg-success/10 text-success'
                          : ''
                      }
                    >
                      {copiedField === 'url' ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />{' '}
                          {t('projects:database.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />{' '}
                          {t('projects:database.copy')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">
                    {t('projects:database.individualCredentials')}
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      {
                        key: 'host',
                        label: t('projects:database.fields.host'),
                        value: credentials.host,
                      },
                      {
                        key: 'port',
                        label: t('projects:database.fields.port'),
                        value: credentials.port,
                      },
                      {
                        key: 'database',
                        label: t('projects:database.fields.database'),
                        value: credentials.database,
                      },
                      {
                        key: 'username',
                        label: t('projects:database.fields.username'),
                        value: credentials.username,
                      },
                    ].map((item) => (
                      <div
                        key={item.key}
                        className="bg-muted rounded-xl p-4 border"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {item.label}
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(item.value, item.key)
                            }
                            className="p-1 text-muted-foreground hover:text-primary rounded transition-colors"
                            title={t('projects:database.copyToClipboard')}
                            aria-label={t('projects:database.copyToClipboard')}
                          >
                            {copiedField === item.key ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <code className="text-sm font-mono break-all">
                          {item.value}
                        </code>
                      </div>
                    ))}

                    <div className="bg-muted rounded-xl p-4 border col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('projects:database.fields.password')}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setShowPassword(!showPassword)}
                            className="p-1 text-muted-foreground hover:text-primary rounded transition-colors"
                            title={
                              showPassword
                                ? t('projects:database.hide')
                                : t('projects:database.show')
                            }
                            aria-label={
                              showPassword
                                ? t('projects:database.hidePassword')
                                : t('projects:database.showPassword')
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              copyToClipboard(credentials.password, 'password')
                            }
                            className="p-1 text-muted-foreground hover:text-primary rounded transition-colors"
                            title={t('projects:database.copyToClipboard')}
                            aria-label={t('projects:database.copyPassword')}
                          >
                            {copiedField === 'password' ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <code className="text-sm font-mono">
                        {showPassword
                          ? credentials.password
                          : t('projects:database.hiddenPassword')}
                      </code>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <div className="flex">
                    <Database className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium">
                        {t('projects:database.howToConnectTitle')}
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        {t('projects:database.howToConnectDescription')}{' '}
                        <code className="px-1.5 py-0.5 bg-primary/10 rounded text-xs font-mono">
                          DATABASE_URL
                        </code>{' '}
                        {t('projects:database.howToConnectSuffix')}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Tables Tab */}
          <TabsContent value="tables" className="p-6 mt-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchTables}
                  disabled={loadingTables}
                  className="text-primary hover:text-primary/80"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-1 ${loadingTables ? 'animate-spin' : ''}`}
                  />
                  {loadingTables
                    ? t('projects:database.refreshing')
                    : t('projects:database.refresh')}
                </Button>
              </div>
            </div>

            {loadingTables && tables.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-6 w-6" />
                <span className="ml-3 text-sm text-muted-foreground">
                  {t('projects:database.loadingTables')}
                </span>
              </div>
            ) : tables.length === 0 ? (
              <div className="text-center py-8 bg-muted rounded-lg">
                <TableIcon className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t('projects:database.noTables')}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {t('projects:database.noTablesHelp')}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                  {tables.map((table) => (
                    <button
                      key={table.name}
                      onClick={() =>
                        setSelectedTable(
                          selectedTable === table.name ? null : table.name,
                        )
                      }
                      className={`p-4 text-left rounded-lg border-2 transition-all cursor-pointer ${
                        selectedTable === table.name
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <TableIcon
                          className={`w-4 h-4 ${selectedTable === table.name ? 'text-primary' : 'text-muted-foreground'}`}
                        />
                        <span className="font-medium truncate">
                          {table.name}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {t('projects:database.cols', {
                            count: table.columnCount,
                          })}
                        </span>
                        <span>{formatBytes(table.sizeBytes)}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Table Data Viewer */}
                {selectedTable && (
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold flex items-center">
                        <TableIcon className="w-4 h-4 mr-2 text-primary" />
                        {t('projects:database.dataFor', {
                          table: selectedTable,
                        })}
                        {tableData && (
                          <span className="ml-2 text-muted-foreground font-normal">
                            (
                            {t('projects:database.rows', {
                              count: tableData.totalCount,
                            })}
                            )
                          </span>
                        )}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            fetchTableData(selectedTable, currentPage)
                          }
                          disabled={loadingData}
                          title={t('projects:database.refresh')}
                          aria-label={t('projects:database.refreshData')}
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${loadingData ? 'animate-spin' : ''}`}
                          />
                        </Button>
                        {tableData?.primaryKey && (
                          <Button size="sm" onClick={openAddModal}>
                            <Plus className="w-4 h-4 mr-1" />
                            {t('projects:database.addRow')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedTable(null)}
                        >
                          {t('common:close')}
                        </Button>
                      </div>
                    </div>

                    {loadingData ? (
                      <div className="flex items-center justify-center py-12">
                        <Spinner className="h-8 w-8" />
                        <span className="ml-3 text-sm text-muted-foreground">
                          {t('projects:database.loadingData')}
                        </span>
                      </div>
                    ) : tableData && tableData.rows.length > 0 ? (
                      <>
                        <div className="overflow-x-auto border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50">
                                {columns.map((col) => (
                                  <TableHead key={col.name} className="text-xs">
                                    <div className="flex items-center gap-1">
                                      <span>{col.name}</span>
                                      {col.name === tableData.primaryKey && (
                                        <Key className="w-3 h-3 text-warning" />
                                      )}
                                    </div>
                                    <span className="text-xs font-normal text-muted-foreground">
                                      {col.type}
                                    </span>
                                  </TableHead>
                                ))}
                                {tableData.primaryKey && (
                                  <TableHead className="text-right text-xs">
                                    {t('projects:database.actions')}
                                  </TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {tableData.rows.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                  {columns.map((col) => (
                                    <TableCell
                                      key={col.name}
                                      className="text-sm"
                                    >
                                      <span
                                        className={`${row[col.name] === null ? 'text-muted-foreground italic' : ''} truncate block max-w-xs`}
                                      >
                                        {formatCellValue(
                                          row[col.name],
                                          t('projects:database.null'),
                                        )}
                                      </span>
                                    </TableCell>
                                  ))}
                                  {tableData.primaryKey && (
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEditModal(row)}
                                        className="text-primary hover:text-primary/80 mr-1"
                                        aria-label={t(
                                          'projects:database.editRow',
                                        )}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openDeleteModal(row)}
                                        className="text-destructive hover:text-destructive/80"
                                        aria-label={t(
                                          'projects:database.deleteRow',
                                        )}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Pagination */}
                        {tableData.totalCount > pageSize && (
                          <div className="mt-4 flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                              {t('projects:database.pagination.showing', {
                                start: currentPage * pageSize + 1,
                                end: Math.min(
                                  (currentPage + 1) * pageSize,
                                  tableData.totalCount,
                                ),
                                total: tableData.totalCount,
                              })}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 0}
                              >
                                {t('projects:database.pagination.previous')}
                              </Button>
                              <span className="text-sm text-muted-foreground">
                                {t('projects:database.pagination.pageOf', {
                                  page: currentPage + 1,
                                  totalPages: Math.ceil(
                                    tableData.totalCount / pageSize,
                                  ),
                                })}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={
                                  currentPage >=
                                  Math.ceil(tableData.totalCount / pageSize) - 1
                                }
                              >
                                {t('projects:database.pagination.next')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12 bg-muted rounded-lg">
                        <Database className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {t('projects:database.noData')}
                        </p>
                        {tableData?.primaryKey && (
                          <Button
                            variant="ghost"
                            onClick={openAddModal}
                            className="mt-3 text-primary hover:text-primary/80"
                          >
                            {t('projects:database.addFirstRow')}
                          </Button>
                        )}
                      </div>
                    )}

                    {!tableData?.primaryKey &&
                      tableData &&
                      tableData.rows.length > 0 && (
                        <p className="mt-2 text-xs text-warning flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          {t('projects:database.noPrimaryKeyWarning')}
                        </p>
                      )}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Add Row Modal */}
      <Dialog
        open={showAddModal && !!selectedTable}
        onOpenChange={setShowAddModal}
      >
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('projects:database.addNewRow')}</DialogTitle>
            <DialogDescription>
              {t('projects:database.tableLabel', {
                table: selectedTable || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 py-4">
            {formError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              {columns.map((col) => (
                <div key={col.name} className="space-y-1">
                  <Label>
                    {col.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({col.type})
                    </span>
                    {!col.nullable && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  <Input
                    type="text"
                    value={formData[col.name] || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, [col.name]: e.target.value })
                    }
                    placeholder={
                      col.defaultValue
                        ? t('projects:database.defaultValue', {
                            value: col.defaultValue,
                          })
                        : col.nullable
                          ? t('projects:database.null')
                          : t('projects:database.required')
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleAddRow} disabled={saving}>
              {saving
                ? t('projects:database.adding')
                : t('projects:database.addRow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Row Modal */}
      <Dialog
        open={showEditModal && !!selectedTable && !!editingRow}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditingRow(null);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('projects:database.editRow')}</DialogTitle>
            <DialogDescription>
              {t('projects:database.tableLabel', {
                table: selectedTable || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 py-4">
            {formError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              {columns.map((col) => (
                <div key={col.name} className="space-y-1">
                  <Label>
                    {col.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({col.type})
                    </span>
                    {col.name === tableData?.primaryKey && (
                      <span className="ml-2 text-xs text-warning">
                        ({t('projects:database.primaryKeyReadOnly')})
                      </span>
                    )}
                  </Label>
                  <Input
                    type="text"
                    value={formData[col.name] || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, [col.name]: e.target.value })
                    }
                    disabled={col.name === tableData?.primaryKey}
                    className={
                      col.name === tableData?.primaryKey
                        ? 'bg-muted text-muted-foreground'
                        : ''
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingRow(null);
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button onClick={handleEditRow} disabled={saving}>
              {saving
                ? t('projects:database.saving')
                : t('projects:database.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Row Confirmation Modal */}
      <Dialog
        open={
          showDeleteModal &&
          !!selectedTable &&
          !!editingRow &&
          !!tableData?.primaryKey
        }
        onOpenChange={(open) => {
          setShowDeleteModal(open);
          if (!open) setEditingRow(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('projects:database.deleteRow')}</DialogTitle>
            <DialogDescription>
              {t('projects:database.tableLabel', {
                table: selectedTable || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              {t('projects:database.deleteRowDescription')}
            </p>
            {editingRow && tableData?.primaryKey && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {tableData.primaryKey}:
                  </span>{' '}
                  <span className="font-mono">
                    {formatCellValue(
                      editingRow[tableData.primaryKey],
                      t('projects:database.null'),
                    )}
                  </span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setEditingRow(null);
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRow}
              disabled={saving}
            >
              {saving
                ? t('projects:database.deleting')
                : t('projects:database.deleteRow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
